/**
 * QuestionCard Component
 *
 * Displays a single question card with all its details including:
 * - Question text, type, level, module badges
 * - Options (for multiple choice questions)
 * - Expected answer
 * - Explanation
 * - Optional edit/delete controls for generated questions
 *
 * @module QuestionCard
 */

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Save, X, Edit, Trash2, Plus, Minus } from 'lucide-react';
import QuestionOptionsList from './QuestionOptionsList';

interface QuestionCardProps {
  question: any;
  index: number;
  variant?: 'sample' | 'generated';
  isEditing?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  contextName?: string;
  editingQuestionData?: any;
  onQuestionTextChange?: (text: string) => void;
  onOptionsChange?: (options: string[]) => void;
  onExpectedAnswerChange?: (answer: string | string[]) => void;
  onExplanationChange?: (explanation: string) => void;
}

/**
 * Component that renders a question card with all question details.
 * Supports both sample questions (read-only) and generated questions (with edit controls).
 *
 * @param question - Question object to display
 * @param index - Original index of the question
 * @param variant - Type of question card ('sample' or 'generated')
 * @param isEditing - Whether the question is currently being edited
 * @param onEdit - Callback when edit button is clicked
 * @param onDelete - Callback when delete button is clicked
 * @param onSave - Callback when save button is clicked (in edit mode)
 * @param onCancel - Callback when cancel button is clicked (in edit mode)
 * @param contextName - Context name for generating unique IDs
 */
export default function QuestionCard({
  question,
  index,
  variant = 'sample',
  isEditing = false,
  onEdit,
  onDelete,
  onSave,
  onCancel,
  contextName,
  editingQuestionData,
  onQuestionTextChange,
  onOptionsChange,
  onExpectedAnswerChange,
  onExplanationChange,
}: Readonly<QuestionCardProps>) {
  const questionId = contextName
    ? `${contextName}-${index}`
    : `question-${index}`;

  const questionLabel =
    variant === 'sample'
      ? `Question ${index + 1}`
      : `Generated Question ${index + 1}`;

  const cardClassName =
    variant === 'generated' ? 'p-4 border-primary/20' : 'p-4';
  const generationModel =
    question.generationModel ||
    question.generation_model ||
    question.context?.generationModel ||
    question.context?.generation_model;

  return (
    <Card key={questionId} className={cardClassName}>
      <div className="space-y-3">
        {/* Question Header */}
        <div
          className={`flex items-center ${variant === 'generated' ? 'justify-between' : 'gap-2'}`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="font-ui">
              {questionLabel}
            </Badge>
            <Badge variant="secondary" className="font-ui">
              {question.level}
            </Badge>
            <Badge variant="secondary" className="font-ui">
              {question.module}
            </Badge>
            <Badge variant="outline" className="font-ui">
              {question.type}
            </Badge>
            <Badge variant="outline" className="font-ui">
              {question.difficulty}
            </Badge>
            {variant === 'generated' && (
              <Badge className="bg-primary/10 text-primary border-primary/20 font-ui">
                AI Generated
              </Badge>
            )}
            {variant === 'generated' && generationModel && (
              <Badge variant="outline" className="font-ui text-xs">
                Model: {generationModel}
              </Badge>
            )}
          </div>

          {/* Question Edit Controls (only for generated questions) */}
          {variant === 'generated' && (
            <div className="flex items-center gap-1">
              {isEditing ? (
                <>
                  <Button size="sm" variant="outline" onClick={onSave}>
                    <Save className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={onCancel}>
                    <X className="h-3 w-3" />
                  </Button>
                </>
              ) : (
                <>
                  {onEdit && (
                    <Button size="sm" variant="outline" onClick={onEdit}>
                      <Edit className="h-3 w-3" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button size="sm" variant="outline" onClick={onDelete}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Question Text */}
        <div>
          {isEditing && variant === 'generated' ? (
            <div className="space-y-2">
              <Label
                htmlFor={`question-text-${questionId}`}
                className="text-xs font-medium font-ui"
              >
                Question Text
              </Label>
              <Textarea
                id={`question-text-${questionId}`}
                value={editingQuestionData?.text || question.text || ''}
                onChange={(e) => onQuestionTextChange?.(e.target.value)}
                className="text-sm min-h-[80px]"
                placeholder="Enter question text"
              />
            </div>
          ) : (
            <p className="text-sm font-medium font-ui">{question.text}</p>
          )}
        </div>

        {/* Options (for multiple choice questions) */}
        {isEditing && variant === 'generated' && editingQuestionData ? (
          <div className="space-y-2">
            <Label className="text-xs font-medium font-ui">Options</Label>
            <div className="space-y-2">
              {(editingQuestionData.options || question.options || []).map(
                (option: string | { text: string;[key: string]: any }, optionIndex: number) => (
                  <div key={`${questionId}-option-${optionIndex}-${typeof option === 'string' ? option : option.text || optionIndex}`} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-6 font-ui">
                      {String.fromCodePoint(65 + optionIndex)}.
                    </span>
                    <Input
                      value={typeof option === 'object' && option !== null && 'text' in option ? (option as any).text : String(option)}

                      onChange={(e) => {
                        const newOptions = [
                          ...(editingQuestionData.options ||
                            question.options ||
                            []),
                        ];
                        newOptions[optionIndex] = e.target.value;
                        onOptionsChange?.(newOptions);
                      }}
                      className="text-sm flex-1"
                      placeholder={`Option ${String.fromCodePoint(65 + optionIndex)}`}
                    />
                    {(editingQuestionData.options || question.options || [])
                      .length > 2 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const newOptions = [
                              ...(editingQuestionData.options ||
                                question.options ||
                                []),
                            ];
                            newOptions.splice(optionIndex, 1);
                            onOptionsChange?.(newOptions);
                          }}
                          className="h-6 w-6 p-0"
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                      )}
                  </div>
                ),
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  const newOptions = [
                    ...(editingQuestionData.options || question.options || []),
                    '',
                  ];
                  onOptionsChange?.(newOptions);
                }}
                className="w-full"
              >
                <Plus className="h-3 w-3 mr-1" />
                <span className="font-ui">Add Option</span>
              </Button>
            </div>
          </div>
        ) : (
          <QuestionOptionsList options={question.options || []} />
        )}

        {/* Diagnostic Tags — shown when tags exist from generation (distractor_tags)
            or from saved options objects. Hidden for writing/speaking modules. */}
        {(() => {
          const module = (question.module || '').toLowerCase();
          if (module === 'writing' || module === 'speaking') return null;

          // Pre-save: distractor_tags is a dict { optionKey: { tag, confidence } }
          const distractorTags: Record<string, { tag?: string; confidence?: string }> =
            question.distractor_tags && typeof question.distractor_tags === 'object'
              ? (question.distractor_tags as Record<string, { tag?: string; confidence?: string }>)
              : {};

          // Post-save: tags are in options objects
          const optionTags: Array<{ key: string; tag: string; confidence?: string | null }> = (
            question.options || []
          )
            .filter(
              (o: any) =>
                o && typeof o === 'object' && Array.isArray(o.tags) && o.tags.length > 0,
            )
            .map((o: any) => ({ key: o.key, tag: o.tags[0], confidence: o.tag_confidence }));

          // Build display list — prefer post-save option tags, fall back to distractor_tags
          const displayTags: Array<{ key: string; tag: string; confidence?: string | null }> =
            optionTags.length > 0
              ? optionTags
              : Object.entries(distractorTags)
                  .filter(([, v]) => v?.tag)
                  .map(([key, v]) => ({ key, tag: v.tag!, confidence: v.confidence }));

          if (displayTags.length === 0) return null;

          return (
            <div className="p-2 rounded border border-violet-200 bg-violet-50/50">
              <h6 className="text-xs font-medium text-violet-700 mb-2 font-ui uppercase tracking-wider">
                Diagnostic Tags
              </h6>
              <div className="flex flex-wrap gap-2">
                {displayTags.map(({ key, tag, confidence }) => (
                  <div
                    key={key}
                    className="flex items-center gap-1 rounded bg-white border border-violet-200 px-2 py-1"
                  >
                    <span className="font-ui text-xs text-muted-foreground">{key}:</span>
                    <code className="font-mono text-xs font-semibold text-violet-700">{tag}</code>
                    {confidence && (
                      <span
                        className={`text-[10px] px-1 rounded font-ui ${
                          confidence === 'high'
                            ? 'bg-emerald-100 text-emerald-700'
                            : confidence === 'low'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {confidence}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Expected Answer */}
        <div className="p-2 bg-primary/10 rounded border border-primary/20">
          <h6 className="text-xs font-medium text-primary mb-1 font-ui">
            Expected Answer
          </h6>
          {isEditing && variant === 'generated' ? (
            <Input
              value={
                (typeof editingQuestionData?.expected_answer === 'object'
                  ? JSON.stringify(editingQuestionData.expected_answer)
                  : editingQuestionData?.expected_answer) ||
                (typeof question.expected_answer === 'object'
                  ? JSON.stringify(question.expected_answer)
                  : question.expected_answer) ||
                ''
              }
              onChange={(e) => onExpectedAnswerChange?.(e.target.value)}
              className="text-sm bg-background border-primary/30"
              placeholder="Enter expected answer"
            />
          ) : (
            <p className="text-sm text-primary font-medium font-ui">
              {typeof question.expected_answer === 'object'
                ? JSON.stringify(question.expected_answer, null, 2)
                : String(question.expected_answer)}
            </p>
          )}
        </div>

        {/* Explanation */}
        <div className="p-2 bg-muted rounded border border-border">
          <h6 className="text-xs font-medium text-foreground mb-1 font-ui">
            Explanation
          </h6>
          {isEditing && variant === 'generated' ? (
            <Textarea
              value={
                editingQuestionData?.explanation || question.explanation || ''
              }
              onChange={(e) => onExplanationChange?.(e.target.value)}
              className="text-sm bg-background border-border min-h-[60px]"
              placeholder="Enter explanation"
            />
          ) : (
            <p className="text-sm text-foreground font-ui">
              {question.explanation || 'No explanation provided'}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
